import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  CHANNEL_LABELS,
  FoundRecordCard,
  isReachable,
  PHONE_STATUS_LABELS,
} from '@horof/shared';
import { Alert, Button, Empty, Input, Segmented, Skeleton, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { CommitteeMembersDocument, matchesMember } from './members-data';

const refetchMembers = { additionalTypenames: ['Member', 'Meter'] };

export function MembersPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'unreachable'>('all');
  const [{ data, error }, refetch] = useQuery({
    query: CommitteeMembersDocument,
    context: refetchMembers,
  });
  const members = useMemo(
    () => (data?.members?.nodes ?? []).flatMap((m) => (m ? [m] : [])),
    [data],
  );
  const unreachable = members.filter((m) => !isReachable(m));
  const shown = filter === 'unreachable' ? unreachable : members;
  const visible = shown.filter((m) => matchesMember(m, search));

  return (
    <div className="page">
      <header className="list-header">
        <div className="title-row">
          <h1>الأعضاء</h1>
          <span className="title-actions">
            <Link to="/members/new">
              <Button type="primary" icon={<PlusOutlined />}>
                عضو جديد
              </Button>
            </Link>
          </span>
        </div>
        <Input
          size="large"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="بحث: الاسم، العداد، الهاتف، العنوان"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Segmented<'all' | 'unreachable'>
          block
          size="large"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: `الكل (${members.length})` },
            {
              value: 'unreachable',
              label: `بدون واتساب (${unreachable.length})`,
            },
          ]}
        />
      </header>
      <FoundRecordCard
        text={search}
        href={(f) =>
          f.billId
            ? `/bills/${f.billId}`
            : f.kind === 'meter'
              ? `/meters/${f.id}`
              : f.memberId
                ? `/members/${f.memberId}`
                : null
        }
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message="تعذّر تحميل الأعضاء"
          action={
            <Button onClick={() => refetch({ requestPolicy: 'network-only' })}>
              أعد المحاولة
            </Button>
          }
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}

      {filter === 'unreachable' && unreachable.length > 0 && (
        <div className="field-hint">
          تُطبع لهؤلاء الفواتير والإشعارات. صحّح أرقامهم ليتوصلوا بها عبر واتساب.
        </div>
      )}

      <ul className="member-list">
        {visible.map((m) => (
          <li key={m.id}>
            <Link to={`/members/${m.id}`} className="member-row">
              <span className="member-main">
                <span className="member-name">{m.fullName}</span>
                <span className="member-sub">
                  {m.meters.nodes
                    .flatMap((meter) => (meter?.label ? [meter.label] : []))
                    .join('، ') || m.memberAddress}
                </span>
              </span>
              <span className="member-tags">
                {m.unnamedShart.totalCount > 0 && (
                  <Tag color="orange">شرط بلا اسم</Tag>
                )}
                {m.notifyChannel === 'NONE' ? (
                  <Tag>{CHANNEL_LABELS.NONE}</Tag>
                ) : (
                  <Tag color={m.phoneStatus === 'OK' ? 'green' : 'orange'}>
                    {PHONE_STATUS_LABELS[m.phoneStatus ?? 'MISSING']}
                  </Tag>
                )}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {data && visible.length === 0 && (
        <Empty description={search ? 'لا يوجد عضو بهذا البحث' : 'لا أحد'} />
      )}
    </div>
  );
}
